"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { HubConnection } from "@microsoft/signalr";
import { SignalRContext } from "./SignalRContext";
import { createHubConnection } from "./connection";
import { useAuthStore } from "@/lib/auth/auth-store";
import { getSessionIdentityKey } from "@/lib/auth/session-identity";

export function SignalRProvider({ children }: { children: ReactNode }) {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  // One connection per signed-in user: token refreshes and feature-flag
  // changes keep it, while an identity change replaces it.
  const identity = useAuthStore((s) =>
    s.sessionState.status === "authenticated"
      ? getSessionIdentityKey(s.sessionState.claims)
      : null,
  );

  useEffect(() => {
    if (identity === null) {
      setConnection((prev) => {
        if (prev) void prev.stop();
        return null;
      });
      setIsConnected(false);
      return;
    }

    const conn = createHubConnection();
    let active = true;
    const updateConnectionState = (connected: boolean) => {
      if (active) setIsConnected(connected);
    };

    conn.onreconnected(() => updateConnectionState(true));
    conn.onreconnecting(() => updateConnectionState(false));
    conn.onclose(() => updateConnectionState(false));

    conn.start().then(
      () => updateConnectionState(true),
      (err: unknown) => {
        if (!active) return;
        console.warn("[SignalR] Failed to connect:", err);
      },
    );
    setConnection(conn);

    return () => {
      // stop() and start() can settle after a replacement has connected.
      active = false;
      void conn.stop();
      setConnection(null);
      setIsConnected(false);
    };
  }, [identity]);

  const value = useMemo(
    () => ({ connection, isConnected }),
    [connection, isConnected],
  );

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
}
